import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import vulnerabilityAPI, {
  Vulnerability,
  VulnerabilityHistory,
  VulnerabilityRiskAcceptance
} from '../services/vulnerabilityAPI';
import {
  ArrowLeft,
  Shield,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  FileText,
  ExternalLink,
  Edit,
  Save,
  X,
  History,
  ShieldAlert,
  Package,
  Code,
  Link2
} from 'lucide-react';

interface VulnerabilityDetailProps {
  vulnerabilityId?: number;
  onBack?: () => void;
}

const VulnerabilityDetail: React.FC<VulnerabilityDetailProps> = ({ vulnerabilityId, onBack }) => {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = vulnerabilityId?.toString() || routeId;
  const [vulnerability, setVulnerability] = useState<(Vulnerability & { history: VulnerabilityHistory[]; riskAcceptances: VulnerabilityRiskAcceptance[] }) | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [showRiskAcceptanceModal, setShowRiskAcceptanceModal] = useState(false);
  const [riskAcceptanceForm, setRiskAcceptanceForm] = useState({
    justification: '',
    business_impact: '',
    compensating_controls: '',
    expiration_date: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [users, setUsers] = useState<{ id: number; name: string; email: string }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      loadVulnerability();
      loadUsers();
    }
  }, [id]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/admin/users?limit=100');
      const data = response.data;
      setUsers(data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadVulnerability = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await vulnerabilityAPI.getVulnerabilityById(parseInt(id));
      setVulnerability(data);
      setEditedNotes(data.remediation_notes || '');
    } catch (error) {
      console.error('Failed to load vulnerability:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!vulnerability) return;
    setIsUpdatingStatus(true);
    try {
      await vulnerabilityAPI.updateVulnerability(vulnerability.id, { status: newStatus });
      await loadVulnerability();
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAssign = async () => {
    if (!vulnerability || selectedUserId === null) return;
    setIsSaving(true);
    try {
      await vulnerabilityAPI.updateVulnerability(vulnerability.id, { assigned_to: selectedUserId });
      setShowAssignModal(false);
      setSelectedUserId(null);
      await loadVulnerability();
      toast.success('Vulnerability assigned successfully');
    } catch (error) {
      console.error('Failed to assign vulnerability:', error);
      toast.error('Failed to assign vulnerability');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!vulnerability) return;
    setIsSaving(true);
    try {
      await vulnerabilityAPI.updateVulnerability(vulnerability.id, { remediation_notes: editedNotes });
      await loadVulnerability();
      setIsEditing(false);
      toast.success('Notes saved successfully');
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestRiskAcceptance = async () => {
    if (!vulnerability) return;
    setIsSaving(true);
    try {
      await vulnerabilityAPI.requestRiskAcceptance(vulnerability.id, riskAcceptanceForm);
      setShowRiskAcceptanceModal(false);
      setRiskAcceptanceForm({
        justification: '',
        business_impact: '',
        compensating_controls: '',
        expiration_date: ''
      });
      await loadVulnerability();
      toast.success('Risk acceptance request submitted');
    } catch (error) {
      console.error('Failed to request risk acceptance:', error);
      toast.error('Failed to submit risk acceptance request');
    } finally {
      setIsSaving(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      zero_day: 'bg-purple-100 text-purple-800 border-purple-200',
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-200',
      low: 'bg-green-100 text-green-800 border-green-200',
      info: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      open: 'bg-red-100 text-red-800',
      in_progress: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      risk_accepted: 'bg-amber-100 text-amber-800',
      false_positive: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getSLAStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      on_track: 'bg-green-50 border-green-200 text-green-800',
      warning: 'bg-amber-50 border-amber-200 text-amber-800',
      critical: 'bg-orange-50 border-orange-200 text-orange-800',
      breached: 'bg-red-50 border-red-200 text-red-800',
      completed: 'bg-blue-50 border-blue-200 text-blue-800'
    };
    return colors[status] || 'bg-gray-50 border-gray-200 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading vulnerability details...</span>
      </div>
    );
  }

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/app/admin/vulnerabilities');
    }
  };

  if (!vulnerability) {
    return (
      <div className="text-center py-12">
        <Shield size={48} className="mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-900">Vulnerability not found</h3>
        <button
          onClick={handleBack}
          className="mt-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(vulnerability.severity)}`}>
              {vulnerability.severity.toUpperCase()}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(vulnerability.status)}`}>
              {vulnerability.status.replace('_', ' ')}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">{vulnerability.title}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            {vulnerability.cve_id && (
              <span className="flex items-center gap-1">
                <ShieldAlert size={14} />
                {vulnerability.cve_id}
              </span>
            )}
            {vulnerability.package_name && (
              <span className="flex items-center gap-1">
                <Package size={14} />
                {vulnerability.package_name} {vulnerability.package_version}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              Discovered {formatDate(vulnerability.discovered_at)}
            </span>
          </div>
        </div>
      </div>

      {vulnerability.slaStatus && (
        <div className={`rounded-lg border p-4 ${getSLAStatusColor(vulnerability.slaStatus.status)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {vulnerability.slaStatus.status === 'breached' ? (
                <XCircle size={24} className="text-red-600" />
              ) : vulnerability.slaStatus.status === 'completed' ? (
                <CheckCircle size={24} className="text-blue-600" />
              ) : (
                <Clock size={24} className={vulnerability.slaStatus.status === 'critical' ? 'text-orange-600' : vulnerability.slaStatus.status === 'warning' ? 'text-amber-600' : 'text-green-600'} />
              )}
              <div>
                <p className="font-semibold">
                  {vulnerability.slaStatus.status === 'completed' ? 'Completed' :
                   vulnerability.slaStatus.status === 'breached' ? `SLA Breached (${Math.abs(vulnerability.slaStatus.daysRemaining)} days overdue)` :
                   `${vulnerability.slaStatus.daysRemaining} days remaining`}
                </p>
                <p className="text-sm opacity-80">
                  Due date: {vulnerability.due_date} | SLA: {vulnerability.sla_days} days
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{vulnerability.slaStatus.percentUsed}%</p>
              <p className="text-sm opacity-80">SLA used</p>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-white/50 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  vulnerability.slaStatus.percentUsed >= 100 ? 'bg-red-500' :
                  vulnerability.slaStatus.percentUsed >= 75 ? 'bg-orange-500' :
                  vulnerability.slaStatus.percentUsed >= 50 ? 'bg-amber-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(vulnerability.slaStatus.percentUsed, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{vulnerability.description || 'No description available.'}</p>

            {vulnerability.references && vulnerability.references.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-2">References</h4>
                <ul className="space-y-2">
                  {vulnerability.references.map((ref, index) => (
                    <li key={index}>
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                      >
                        <ExternalLink size={14} />
                        {ref.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Remediation Notes</h3>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  <Edit size={14} />
                  Edit
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditedNotes(vulnerability.remediation_notes || '');
                    }}
                    className="flex items-center gap-1 text-gray-600 hover:text-gray-800 text-sm font-medium"
                  >
                    <X size={14} />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNotes}
                    disabled={isSaving}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50"
                  >
                    <Save size={14} />
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            {isEditing ? (
              <textarea
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add remediation notes..."
              />
            ) : (
              <p className="text-gray-700 whitespace-pre-wrap">
                {vulnerability.remediation_notes || 'No remediation notes yet.'}
              </p>
            )}

            {vulnerability.fixed_version && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Fix available:</strong> Update to version {vulnerability.fixed_version}
                </p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <History size={20} />
              Activity Log
            </h3>
            {vulnerability.history && vulnerability.history.length > 0 ? (
              <div className="space-y-4">
                {vulnerability.history.map((entry) => (
                  <div key={entry.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0">
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <User size={14} className="text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-gray-900">
                          {entry.performer?.name || 'System'}
                        </span>
                        <span className="text-gray-500">
                          {entry.action.replace(/_/g, ' ')}
                        </span>
                      </div>
                      {entry.field_name && (
                        <p className="text-sm text-gray-600 mt-1">
                          Changed <span className="font-medium">{entry.field_name}</span> from{' '}
                          <span className="text-gray-500">{entry.old_value || '(empty)'}</span> to{' '}
                          <span className="text-gray-900">{entry.new_value}</span>
                        </p>
                      )}
                      {entry.notes && (
                        <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">{formatDate(entry.performed_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No activity recorded yet.</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              {vulnerability.status === 'open' && (
                <button
                  onClick={() => handleStatusChange('in_progress')}
                  disabled={isUpdatingStatus}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingStatus ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : null}
                  {isUpdatingStatus ? 'Updating...' : 'Start Working'}
                </button>
              )}
              {vulnerability.status === 'in_progress' && (
                <button
                  onClick={() => handleStatusChange('resolved')}
                  disabled={isUpdatingStatus}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingStatus ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <CheckCircle size={16} />
                  )}
                  {isUpdatingStatus ? 'Updating...' : 'Mark Resolved'}
                </button>
              )}
              {(vulnerability.status === 'open' || vulnerability.status === 'in_progress') && (
                <>
                  <button
                    onClick={() => setShowAssignModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                  >
                    <User size={16} />
                    Assign to Someone
                  </button>
                  <button
                    onClick={() => setShowRiskAcceptanceModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
                  >
                    <ShieldAlert size={16} />
                    Request Risk Acceptance
                  </button>
                  <button
                    onClick={() => handleStatusChange('false_positive')}
                    disabled={isUpdatingStatus}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingStatus ? (
                      <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                    ) : null}
                    {isUpdatingStatus ? 'Updating...' : 'Mark as False Positive'}
                  </button>
                </>
              )}
              {vulnerability.status === 'resolved' && (
                <button
                  onClick={() => handleStatusChange('open')}
                  disabled={isUpdatingStatus}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingStatus ? (
                    <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                  ) : null}
                  {isUpdatingStatus ? 'Updating...' : 'Reopen'}
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Vulnerability ID</dt>
                <dd className="text-sm font-medium text-gray-900">{vulnerability.vulnerability_id}</dd>
              </div>
              {vulnerability.cvss_score && (
                <div>
                  <dt className="text-sm text-gray-500">CVSS Score</dt>
                  <dd className="text-sm font-medium text-gray-900">{vulnerability.cvss_score}</dd>
                </div>
              )}
              {vulnerability.cwe_id && (
                <div>
                  <dt className="text-sm text-gray-500">CWE</dt>
                  <dd className="text-sm font-medium text-gray-900">{vulnerability.cwe_id}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-gray-500">Source</dt>
                <dd className="text-sm font-medium text-gray-900 capitalize">{vulnerability.source.replace('_', ' ')}</dd>
              </div>
              {vulnerability.affected_component && (
                <div>
                  <dt className="text-sm text-gray-500">Affected Component</dt>
                  <dd className="text-sm font-medium text-gray-900">{vulnerability.affected_component}</dd>
                </div>
              )}
              {vulnerability.file_path && (
                <div>
                  <dt className="text-sm text-gray-500">File Path</dt>
                  <dd className="text-sm font-medium text-gray-900 break-all">{vulnerability.file_path}</dd>
                </div>
              )}
              {vulnerability.assignee && (
                <div>
                  <dt className="text-sm text-gray-500">Assigned To</dt>
                  <dd className="text-sm font-medium text-gray-900">{vulnerability.assignee.name}</dd>
                </div>
              )}
            </dl>
          </div>

          {vulnerability.riskAcceptances && vulnerability.riskAcceptances.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Acceptances</h3>
              <div className="space-y-4">
                {vulnerability.riskAcceptances.map((ra) => (
                  <div key={ra.id} className={`p-3 rounded-lg border ${
                    ra.status === 'approved' ? 'bg-green-50 border-green-200' :
                    ra.status === 'rejected' ? 'bg-red-50 border-red-200' :
                    'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${
                        ra.status === 'approved' ? 'text-green-800' :
                        ra.status === 'rejected' ? 'text-red-800' :
                        'text-amber-800'
                      }`}>
                        {ra.status.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-500">{formatDate(ra.requested_at)}</span>
                    </div>
                    <p className="text-sm text-gray-700">{ra.justification}</p>
                    {ra.requester && (
                      <p className="text-xs text-gray-500 mt-2">
                        Requested by {ra.requester.name}
                      </p>
                    )}
                    {ra.reviewer && ra.reviewed_at && (
                      <p className="text-xs text-gray-500">
                        Reviewed by {ra.reviewer.name} on {formatDate(ra.reviewed_at)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showRiskAcceptanceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Request Risk Acceptance</h3>
              <p className="text-sm text-gray-500 mt-1">
                Submit a request to accept this vulnerability risk
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Justification <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={riskAcceptanceForm.justification}
                  onChange={(e) => setRiskAcceptanceForm({ ...riskAcceptanceForm, justification: e.target.value })}
                  className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Explain why this risk should be accepted..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Impact
                </label>
                <textarea
                  value={riskAcceptanceForm.business_impact}
                  onChange={(e) => setRiskAcceptanceForm({ ...riskAcceptanceForm, business_impact: e.target.value })}
                  className="w-full h-20 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the business impact of not remediating..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Compensating Controls
                </label>
                <textarea
                  value={riskAcceptanceForm.compensating_controls}
                  onChange={(e) => setRiskAcceptanceForm({ ...riskAcceptanceForm, compensating_controls: e.target.value })}
                  className="w-full h-20 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="List any compensating controls in place..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={riskAcceptanceForm.expiration_date}
                  onChange={(e) => setRiskAcceptanceForm({ ...riskAcceptanceForm, expiration_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowRiskAcceptanceModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestRiskAcceptance}
                disabled={!riskAcceptanceForm.justification || isSaving}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
              >
                {isSaving ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Assign Vulnerability</h3>
              <p className="text-sm text-gray-500 mt-1">
                Select a team member to assign this vulnerability to
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign to
              </label>
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a user...</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
              {vulnerability?.assignee && (
                <p className="text-sm text-gray-500 mt-2">
                  Currently assigned to: <span className="font-medium">{vulnerability.assignee.name}</span>
                </p>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedUserId(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedUserId || isSaving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {isSaving ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VulnerabilityDetail;
